import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtMoney, fmtPct } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useSEO, jsonLdBreadcrumb } from "@/lib/seo";
import { ArrowRight, Layers } from "lucide-react";

export default function Portfolios() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useSEO({
    title: "Vehicle Note Portfolios for Sale — Bulk Auto-Note Purchases | FinanceLocal",
    description: "Browse portfolios of vehicle-backed notes for sale on FinanceLocal. Aggregate stats — UPB, average yield, seasoning, geographic distribution — for every bundle. Request access to view individual loan documents.",
    canonical: "https://myfinancelocal.com/portfolios",
    keywords: "vehicle note portfolios, auto note bulk sale, cash-flow note portfolios, note portfolio buyers, UPB, portfolio yield",
    jsonLd: jsonLdBreadcrumb([
      { name: "Home", url: "https://myfinancelocal.com/" },
      { name: "Portfolios", url: "https://myfinancelocal.com/portfolios" },
    ]),
  });

  useEffect(() => {
    api.get("/marketplace/portfolios")
      .then((r) => setItems(r.data.items || []))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="page-portfolios" className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10">
      <div className="border-b border-border pb-6 mb-8">
        <div className="font-overline text-accent">Marketplace · Bulk Purchases</div>
        <h1 className="font-display text-4xl sm:text-5xl mt-2">Note Portfolios for Sale</h1>
        <p className="text-muted-foreground mt-2 max-w-3xl">
          Bundled vehicle-note portfolios (minimum {5} notes each). Aggregate stats are public — individual loan documents unlock after your access request is approved by the seller.
        </p>
      </div>

      {loading && <div className="text-muted-foreground" data-testid="portfolios-loading">Loading portfolios…</div>}
      {!loading && items.length === 0 && (
        <div className="border border-border p-12 text-center text-muted-foreground" data-testid="portfolios-empty">
          No portfolios are listed yet. Check back soon.
        </div>
      )}
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="portfolios-grid">
        {items.map((p) => (
          <Link key={p.portfolio_id} to={`/portfolios/${p.portfolio_id}`}
                data-testid={`portfolio-card-${p.portfolio_id}`}
                className="border border-border p-6 hover:border-primary transition-colors flex flex-col">
            <div className="flex items-center gap-2 font-overline text-accent">
              <Layers className="w-4 h-4" /> Portfolio
            </div>
            <h2 className="font-display text-2xl mt-2 leading-tight">{p.title}</h2>
            {p.description && <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{p.description}</p>}
            <div className="grid grid-cols-2 gap-x-6 gap-y-2 border-t border-border mt-4 pt-4 text-sm">
              <Stat label="Notes" value={p.summary?.note_count} />
              <Stat label="Total UPB" value={fmtMoney(p.summary?.total_upb || 0)} />
              <Stat label="Avg Yield" value={fmtPct(p.summary?.average_yield || 0)} highlight />
              <Stat label="Asking" value={fmtMoney(p.asking_price || 0)} />
            </div>
            <div className="mt-auto pt-4 flex items-center gap-2 text-xs font-overline text-primary">
              View Portfolio <ArrowRight className="w-3.5 h-3.5" />
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <div>
      <div className="font-overline text-muted-foreground text-[10px]">{label}</div>
      <div className={`font-mono-data ${highlight ? "text-primary" : ""}`}>{value ?? "—"}</div>
    </div>
  );
}
